import React from "react";

/**
 * 捕获 React.lazy 动态加载 chunk 失败的错误（ChunkLoadError）。
 *
 * 常见场景：前端重新构建部署后，浏览器仍持有旧的 index.html / 主 bundle，
 * 其中引用的懒加载路由 chunk 文件名（hash）已经不存在于服务器上（404），
 * 导致 import() 失败抛出未捕获的异常，页面渲染为空白，必须手动刷新才能
 * 重新拉取最新的 index.html 并恢复正常。
 *
 * 这里捕获该错误后自动刷新一次页面（用 sessionStorage 记录时间戳防止刷新死循环）。
 */
const RELOAD_FLAG_KEY = "chunk_reload_attempted_at";
const RELOAD_COOLDOWN_MS = 10000; // 10秒内不重复自动刷新，避免持续报错时死循环

function isChunkLoadError(error) {
  if (!error) return false;
  const message = String(error.message || "");
  return (
    error.name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
}

export default class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      const lastAttempt = Number(window.sessionStorage.getItem(RELOAD_FLAG_KEY) || 0);
      const now = Date.now();
      if (now - lastAttempt > RELOAD_COOLDOWN_MS) {
        window.sessionStorage.setItem(RELOAD_FLAG_KEY, String(now));
        window.location.reload();
        return;
      }
    }
    // 非 chunk 加载错误，或短时间内已经自动刷新过仍然报错，打印出来便于排查
    console.error("App crashed:", error);
  }

  render() {
    if (this.state.hasError) {
      // 自动刷新期间不展示任何内容（reload 会很快发生）
      return null;
    }
    return this.props.children;
  }
}
